import type { PwaPlatform } from "./pwa-install";

export const GET_APP_SHEET_STORAGE_KEY = "get-app-sheet-last-shown";
export const GET_APP_SHEET_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
export const GET_APP_AFTER_PICKS_DELAY_MS = 1500;
export const GET_APP_LANDING_DELAY_MS = 2500;

export type AppStorePlatform = "ios" | "android";

/** landing: first look at the site. picks_complete: the ballot was just finished. menu: the user asked for it. */
export type GetAppMoment = "landing" | "picks_complete" | "menu";

export interface StoreListingLinks {
  iosUrl: string | null;
  androidUrl: string | null;
}

export function appStorePlatform(platform: PwaPlatform): AppStorePlatform | null {
  if (platform.startsWith("ios-")) {
    return "ios";
  }
  if (platform.startsWith("android-")) {
    return "android";
  }
  return null;
}

export function storeUrlForPlatform(
  platform: PwaPlatform,
  links: StoreListingLinks,
): string | null {
  const store = appStorePlatform(platform);
  if (store === "ios") {
    return links.iosUrl;
  }
  if (store === "android") {
    return links.androidUrl;
  }
  return null;
}

const LANDING_SECTIONS = ["/shows", "/blog", "/guides", "/award-shows"];

/** Pages where a first-time visitor may be offered the app. Invites, sign-in, parties, and ballots stay uninterrupted. */
export function isGetAppLandingPath(pathname: string): boolean {
  if (pathname === "/" || pathname === "/parties") {
    return true;
  }
  return LANDING_SECTIONS.some(
    (section) => pathname === section || pathname.startsWith(`${section}/`),
  );
}

export function parseGetAppLastShown(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) {
    return null;
  }
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : null;
}

export function shouldAutoShowGetAppSheet({
  platform,
  isNativeShell,
  iosUrl,
  androidUrl,
  lastShownAt,
  now,
  moment,
}: StoreListingLinks & {
  platform: PwaPlatform;
  isNativeShell: boolean;
  lastShownAt: number | null;
  now: number;
  moment: Exclude<GetAppMoment, "menu">;
}): boolean {
  if (isNativeShell) {
    return false;
  }
  if (!storeUrlForPlatform(platform, { iosUrl, androidUrl })) {
    return false;
  }
  if (moment === "picks_complete") {
    return true;
  }
  return lastShownAt === null || now - lastShownAt >= GET_APP_SHEET_COOLDOWN_MS;
}
